import { Button, Form, Radio, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';
import DisplayDefault from '../DisplayDefault';
import {
  displayMultiChoice,
  getSettingOptions,
  getUnifiedSetting,
  notNullOrUndefined,
} from '../utils';

const { Title, Text } = Typography;

function EditMultiChoiceModal({
  systemConfig,
  tenantConfig,
  deviceConfig,
  saving,
  handleFinish,
  onCancel,
  table,
  settingKey,
  label,
  SelectComponent,
  choices,
}) {
  const [form] = Form.useForm();
  useEffect(() => {
    const [value, src] = getUnifiedSetting(
      systemConfig,
      tenantConfig,
      deviceConfig,
      table,
      settingKey,
    );
    form.setFieldsValue({
      useDefault: src,
      [settingKey]: value,
    });
  }, [systemConfig, tenantConfig, deviceConfig, , form, table, settingKey]);
  const onFinish = useCallback(
    (values) => {
      const { [settingKey]: selected, useDefault } = values;
      let value;
      if (deviceConfig && useDefault === 'device') {
        value = selected;
      } else if (!deviceConfig && useDefault === 'tenant') {
        value = selected;
      } else {
        value = null;
      }
      handleFinish({
        [table]: {
          _id: new Date().toISOString(),
          [settingKey]: value,
        },
      });
    },
    [handleFinish, table, settingKey, deviceConfig],
  );
  const [systemAttr, tenantAttr, deviceAttr] = useMemo(() => {
    return getSettingOptions(
      systemConfig,
      tenantConfig,
      deviceConfig,
      table,
      settingKey,
    );
  }, [systemConfig, tenantConfig, deviceConfig, table, settingKey]);
  return (
    <>
      <Title level={3} style={{ textAlign: 'center', marginBottom: 16 }}>
        {label}
      </Title>
      <Form layout="vertical" form={form} onFinish={onFinish}>
        <Form.Item name="useDefault">
          <Radio.Group
            style={{
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {notNullOrUndefined(systemAttr) && (
              <Radio
                disabled={!!deviceConfig && notNullOrUndefined(tenantAttr)}
                value="system"
                style={{ marginBottom: 8 }}
              >
                {`System default (${displayMultiChoice(systemAttr, choices)})`}
              </Radio>
            )}
            {!!deviceConfig && notNullOrUndefined(tenantAttr) && (
              <Radio
                value="tenant"
                style={{ marginBottom: 8 }}
              >{`Tenant default (${displayMultiChoice(
                tenantAttr,
                choices,
              )})`}</Radio>
            )}
            {deviceConfig ? (
              <Radio value="device">Specify for this device</Radio>
            ) : (
              <Radio value="tenant">Specify for this tenant</Radio>
            )}
          </Radio.Group>
        </Form.Item>
        <Form.Item
          shouldUpdate={(prevValues, curValues) =>
            prevValues.useDefault !== curValues.useDefault
          }
          noStyle
        >
          {({ getFieldValue }) =>
            getFieldValue('useDefault') == 'system' ? (
              <DisplayDefault
                label={`${label} Enabled`}
                value={displayMultiChoice(systemAttr, choices)}
              />
            ) : getFieldValue('useDefault') == 'tenant' ? (
              <>
                {deviceConfig ? (
                  <DisplayDefault
                    label={`${label} Enabled`}
                    value={displayMultiChoice(tenantAttr, choices)}
                  />
                ) : (
                  <Form.Item
                    name={settingKey}
                    label={label}
                    rules={[
                      { required: true, message: 'This field is required' },
                    ]}
                  >
                    <SelectComponent disabled={saving} />
                  </Form.Item>
                )}
              </>
            ) : (
              <Form.Item
                name={settingKey}
                label={label}
                rules={[{ required: true, message: 'This field is required' }]}
              >
                <SelectComponent disabled={saving} />
              </Form.Item>
            )
          }
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldsError }) => {
            const errorList = getFieldsError();
            let showError = false;
            errorList.forEach((errors) => {
              if (errors.errors.length) {
                showError = true;
              }
            });
            return (
              showError && (
                <Text type="danger" style={{ marginTop: 16 }}>
                  Please correct the errors above
                </Text>
              )
            );
          }}
        </Form.Item>
        <Form.Item>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={onCancel}
              htmlType="button"
              disabled={saving}
              style={{ marginRight: 16 }}
            >
              Cancel
            </Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form.Item>
      </Form>
    </>
  );
}

export default EditMultiChoiceModal;
